import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axios';

const NewLeadNotificationPopup = ({ onViewLead, onNewLeadDetected, notificationType = 'lead_forwarded' }) => {
  const [notifications, setNotifications] = useState([]);
  const [dismissingIds, setDismissingIds] = useState(new Set());

  // Use ref to track seen notification IDs (persists across renders without causing re-renders)
  const seenNotificationIds = useRef(new Set());
  const hasInitialized = useRef(false);

  // Load seen IDs from localStorage on mount
  useEffect(() => {
    const storageKey = `seen_notifications_${notificationType}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        seenNotificationIds.current = new Set(parsed);
      } catch (e) {
        seenNotificationIds.current = new Set();
      }
    }
    hasInitialized.current = true;
  }, [notificationType]);

  useEffect(() => {
    // Check for new notifications every 10 seconds (increased to reduce load)
    const interval = setInterval(() => {
      fetchNotifications();
    }, 10000);

    // Initial check after a small delay to ensure initialization
    setTimeout(() => {
      fetchNotifications();
    }, 1000);

    return () => clearInterval(interval);
  }, [notificationType]);

  const fetchNotifications = async () => {
    if (!hasInitialized.current) return;

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get('/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const activeNotifications = response.data.notifications.filter(
          n => n.type === notificationType && !n.isDismissed
        );

        if (activeNotifications.length > 0) {
          // Find truly NEW notifications (not in seenNotificationIds)
          const newNotifications = activeNotifications.filter(
            n => !seenNotificationIds.current.has(n._id)
          );

          setNotifications(activeNotifications);

          // Only notify parent if there are truly new notifications
          if (newNotifications.length > 0 && onNewLeadDetected) {
            console.log('🔔 Genuinely new notifications:', newNotifications.length);
            onNewLeadDetected(newNotifications.map(n => n.leadId));
          }

          // Mark all current notifications as seen
          activeNotifications.forEach(n => seenNotificationIds.current.add(n._id));

          // Save to localStorage
          const storageKey = `seen_notifications_${notificationType}`;
          localStorage.setItem(storageKey, JSON.stringify([...seenNotificationIds.current]));
        } else {
          setNotifications([]);
        }
      }
    } catch (error) {
      // Silently handle errors
    }
  };

  const handleDismiss = async (notificationId, leadId) => {
    // Add to dismissing state for fade-out animation
    setDismissingIds(prev => {
      const newSet = new Set(prev);
      newSet.add(notificationId);
      return newSet;
    });

    // Call API to dismiss notification
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      await axios.patch(
        `/notifications/${notificationId}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ Notification dismissed in database');
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }

    // After animation, remove from UI
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n._id !== notificationId));

      // Remove from dismissing state
      setDismissingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }, 300);
  };

  const handleViewLead = (notification) => {
    // First dismiss the notification
    handleDismiss(notification._id, notification.leadId._id);
    // Then call the parent's view handler
    setTimeout(() => {
      onViewLead(notification.leadId);
    }, 100);
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-20 right-6 z-[9999] space-y-3 max-w-sm">
      {notifications.map(notification => {
        const lead = notification.leadId;
        if (!lead) return null;

        const isDismissing = dismissingIds.has(notification._id);
        return (
          <div
            key={notification._id}
            className={`bg-white rounded-xl shadow-2xl border-l-4 border-green-500 overflow-hidden transition-all duration-300 ${isDismissing ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
              }`}
            style={{
              animation: isDismissing ? 'none' : 'slideIn 0.5s ease-out, pulse 2s ease-in-out infinite'
            }}
          >
            {/* Header */}
            <div className={`px-4 py-3 flex items-center justify-between ${notificationType === 'quotation_forwarded_to_sales'
              ? 'bg-gradient-to-r from-blue-500 to-indigo-600'
              : notificationType === 'amendment_requested'
                ? 'bg-gradient-to-r from-orange-500 to-red-600'
                : 'bg-gradient-to-r from-green-500 to-emerald-600'
              }`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-2xl">
                    {notificationType === 'quotation_forwarded_to_sales' ? '📄' :
                      notificationType === 'amendment_requested' ? '🔄' : '🎯'}
                  </span>
                </div>
                <div>
                  <h3 className="font-bold text-white text-base">
                    {notificationType === 'quotation_forwarded_to_sales'
                      ? 'New Quotation Received!'
                      : notificationType === 'amendment_requested'
                        ? 'Amendment Request Received!'
                        : 'New Lead Received!'}
                  </h3>
                  <p className="text-xs text-green-100">
                    {notificationType === 'quotation_forwarded_to_sales'
                      ? 'From Reservation Team • Just now'
                      : notificationType === 'amendment_requested'
                        ? 'From Sales Team • Just now'
                        : 'From Sales Team • Just now'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDismiss(notification._id, lead._id)}
                className="text-white hover:text-gray-200 transition-colors p-1"
                title="Dismiss"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content - Clickable */}
            <div
              onClick={() => handleViewLead(notification)}
              className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <div className="space-y-3">
                {/* Customer Name */}
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 text-sm">👤</span>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Customer Name</p>
                    <p className="font-semibold text-gray-900">{lead.customerName}</p>
                  </div>
                </div>

                {/* Contact Details - Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 text-sm">📧</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-gray-800 text-xs truncate">{lead.email || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 text-sm">📱</span>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-medium text-gray-800 text-xs">{lead.contactNumber || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Destination - Show for all leads */}
                {lead.destination && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 text-sm">📍</span>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Destination</p>
                      <p className="font-medium text-gray-800">{lead.destination}</p>
                    </div>
                  </div>
                )}

                {/* Tour Details - Only for tour bookings */}
                {lead.leadType === 'tour_booking' && (
                  <>
                    {/* Tour Type */}
                    {lead.tourType && (
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 text-sm">{lead.tourType === 'domestic' ? '🇮🇳' : '🌏'}</span>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Tour Type</p>
                          <p className="font-medium text-gray-800 capitalize">{lead.tourType}</p>
                        </div>
                      </div>
                    )}

                    {/* Travel Date & Nights */}
                    <div className="grid grid-cols-2 gap-3">
                      {lead.travelDate && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400 text-sm">📅</span>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Travel Date</p>
                            <p className="font-medium text-gray-800 text-sm">
                              {new Date(lead.travelDate).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      )}

                      {lead.numberOfNights && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400 text-sm">🌙</span>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Duration</p>
                            <p className="font-medium text-gray-800">{lead.numberOfNights}N / {parseInt(lead.numberOfNights) + 1}D</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pax & Children */}
                    <div className="grid grid-cols-2 gap-3">
                      {lead.numberOfPax && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400 text-sm">👥</span>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Adults</p>
                            <p className="font-medium text-gray-800">{lead.numberOfPax} Pax</p>
                          </div>
                        </div>
                      )}

                      {lead.numberOfChildren > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400 text-sm">👶</span>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Children</p>
                            <p className="font-medium text-gray-800">{lead.numberOfChildren} Child{lead.numberOfChildren > 1 ? 'ren' : ''}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Tour Code & Inquiry Type */}
                    <div className="grid grid-cols-2 gap-3">
                      {lead.tourCode && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400 text-sm">🎫</span>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Tour Code</p>
                            <p className="font-mono font-semibold text-blue-600 text-sm">{lead.tourCode}</p>
                          </div>
                        </div>
                      )}

                      {lead.inquiryType && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400 text-sm">📞</span>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Inquiry Type</p>
                            <p className="font-medium text-gray-800">{lead.inquiryType}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Special Note */}
                    {lead.specialNote && (
                      <div className="flex items-start gap-2 bg-yellow-50 p-2 rounded border border-yellow-200">
                        <span className="text-yellow-600 text-sm">📝</span>
                        <div className="flex-1">
                          <p className="text-xs text-yellow-700 font-medium">Special Note</p>
                          <p className="text-sm text-gray-800 line-clamp-2">{lead.specialNote}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Other Services Details */}
                {lead.leadType === 'other_services' && lead.serviceType && (
                  <div className="flex items-start gap-2 bg-blue-50 p-2 rounded border border-blue-200">
                    <span className="text-blue-600 text-sm">📋</span>
                    <div className="flex-1">
                      <p className="text-xs text-blue-700 font-medium">Service Type</p>
                      <p className="text-sm text-gray-800 capitalize">{lead.serviceType.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                )}

                {/* Show quotation number for quotation notifications */}
                {notificationType === 'quotation_forwarded_to_sales' && notification.data?.quotationNumber && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 text-sm">📄</span>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Quotation Number</p>
                      <p className="font-mono font-semibold text-blue-600 text-sm">{notification.data.quotationNumber}</p>
                    </div>
                  </div>
                )}

                {/* Show amendment details for amendment notifications */}
                {notificationType === 'amendment_requested' && (
                  <>
                    {notification.data?.quotationNumber && (
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 text-sm">📄</span>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Quotation Number</p>
                          <p className="font-mono font-semibold text-orange-600 text-sm">{notification.data.quotationNumber}</p>
                        </div>
                      </div>
                    )}
                    {notification.data?.changes && (
                      <div className="flex items-start gap-2 mt-2">
                        <span className="text-gray-400 text-sm">📝</span>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Requested Changes</p>
                          <p className="text-sm text-gray-800 line-clamp-2">{notification.data.changes}</p>
                        </div>
                      </div>
                    )}
                    {notification.data?.urgency && (
                      <div className="flex items-start gap-2 mt-2">
                        <span className="text-gray-400 text-sm">⚡</span>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Urgency</p>
                          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${notification.data.urgency === 'high'
                            ? 'bg-red-100 text-red-700'
                            : notification.data.urgency === 'normal'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                            }`}>
                            {notification.data.urgency.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Click to view hint */}
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center italic">
                    💡 Click anywhere to view full details
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-4 py-3 bg-white border-t flex gap-2">
              <button
                onClick={() => handleViewLead(notification)}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {notificationType === 'quotation_forwarded_to_sales' ? 'View Quotation' :
                  notificationType === 'amendment_requested' ? 'View Request' : 'View Lead'}
              </button>
              <button
                onClick={() => handleDismiss(notification._id, lead._id)}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all text-sm font-medium"
              >
                Later
              </button>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default NewLeadNotificationPopup;
